import { Injectable, signal, inject, computed, Signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, NavigationError, IsActiveMatchOptions, isActive } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, take } from 'rxjs';
import { goBackNavItems, navigationItems } from '../utils/nav-items';
import { observeMaxWidth } from '../utils/observe-max-width';

export interface IRouteState {
  name: string;
  url: string;
}

const HIDE_SIDENAV_ROUTES = ['parts-shop/checkout', 'machines/machine-sharing/invitation'];
const HIDE_HEADER_ROUTES = ['machines/machine-sharing/invitation'];
const HIDE_HEADER_ON_MOBILE_ROUTES = ['machines/details'];
const SHOW_HELP_MENU_ROUTES = ['machines/machine-sharing/invitation'];
const ROUTE_MATCH_OPTIONS: IsActiveMatchOptions = {
  paths: 'subset',
  queryParams: 'subset',
  fragment: 'ignored',
  matrixParams: 'ignored',
};

@Injectable()
export class RoutingStateService {
  readonly router = inject(Router);
  readonly title = inject(Title);
  readonly translator = inject(TranslateService);
  readonly previousRoute = signal<IRouteState>({ name: 'left_sidenav.dashboard', url: '/dashboard' });
  readonly currentRoute = signal<IRouteState>({ name: '', url: '' });
  readonly isMachineSharingInvitationPage = isActive(
    'machines/machine-sharing/invitation',
    this.router,
    ROUTE_MATCH_OPTIONS
  );
  readonly isHideSidenavRoute: Signal<boolean>;
  readonly isHideHeaderRoute: Signal<boolean>;
  readonly isHideHeaderOnMobileRoute: Signal<boolean>;
  readonly showHelpMenu: Signal<boolean>;

  constructor() {
    const isMobileOrTablet = observeMaxWidth(960);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationError || event instanceof NavigationEnd),
        map(event => event as NavigationError | NavigationEnd)
      )
      .subscribe(event => {
        if (event instanceof NavigationError) {
          console.error('Navigation error', event);
          if (
            event.error instanceof Error &&
            (event.error.name === 'ChunkLoadError' ||
              event.error.message.toLowerCase().startsWith('failed to fetch dynamically imported module'))
          ) {
            window.location.assign(event.url);
          }
        }

        if (event instanceof NavigationEnd) {
          this.setPreviousRoute({ ...this.previousRoute(), url: this.currentRoute().url });
          this.setCurrentRoute({ ...this.currentRoute(), url: event.url });

          this.setTitle(event);
        }
      });

    this.showHelpMenu = this.createActiveRouteSignal(SHOW_HELP_MENU_ROUTES);
    this.isHideSidenavRoute = this.createActiveRouteSignal(HIDE_SIDENAV_ROUTES);
    this.isHideHeaderRoute = this.createActiveRouteSignal(HIDE_HEADER_ROUTES);
    const isHideHeaderMobileRoute = this.createActiveRouteSignal(HIDE_HEADER_ON_MOBILE_ROUTES);
    this.isHideHeaderOnMobileRoute = computed(() => isHideHeaderMobileRoute() && isMobileOrTablet());
  }

  private setPreviousRoute(route: IRouteState) {
    this.mapRouteName(route);
    this.previousRoute.set(route);
  }

  private setCurrentRoute(route: IRouteState) {
    this.mapRouteName(route);
    this.currentRoute.set(route);
  }

  private setTitle(event: NavigationEnd) {
    if (event.url === '/dashboard/digital-solution-tour') {
      this.title.setTitle('CLAAS Digital Solution Tour 2025');
      return;
    }

    const navigationRoute = navigationItems.find(item => event.url?.includes(item.route || ''));
    const titleText = navigationRoute?.label ?? (event.url === '/' ? 'left_sidenav.dashboard' : '');
    if (!titleText) {
      this.title.setTitle('CLAAS connect');
      return;
    }

    this.translator
      .get(titleText)
      .pipe(
        filter(text => !text.includes('left_sidenav.')),
        take(1)
      )
      .subscribe(translatedTitleText => {
        this.title.setTitle('CLAAS connect | ' + (translatedTitleText || ''));
      });
  }

  private createActiveRouteSignal(routes: string[]): Signal<boolean> {
    const activeRoutes = routes.map(route => isActive(route, this.router, ROUTE_MATCH_OPTIONS));
    return computed(() => activeRoutes.some(active => active()));
  }

  private mapRouteName(route: IRouteState) {
    const navigationRoute = goBackNavItems.find(item => route.url.includes(item.route));
    if (navigationRoute) {
      route.name = navigationRoute.text;
    }
  }
}
